import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Shield, Users, BookOpen, Brain, AlertTriangle,
  CheckCircle, Info, Lightbulb, Target, Calendar, Clock,
  User, MessageCircle, Phone, Search, Star, Award,
  Activity, TrendingUp, Zap, Eye, EyeOff, HelpCircle,
  Stethoscope, UserCheck, Lock, Globe, Clipboard,
  Trophy, Gift, Gamepad2, ArrowRight, Plus, Minus,
  Sparkles, Coffee, Wind
} from 'lucide-react';
import { sexualHealthData } from './SexualHealthData';
import {  AnatomySection, renderContraception , renderSafety, renderSTI, renderPleasure, } from './SexualHealthEducation2.js';

import Layout from '../layout';
import SexualHealthCarousels from './SexualHealthCarousels';
import DailyTipsCarousel from './DailyTipsCarousel';

const SexualHealthEducation = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const [periodData, setPeriodData] = useState({
    lastPeriodDate: '',
    cycleLength: 28,
    periodLength: 5,
    symptoms: []
  });
  const [predictions, setPredictions] = useState(null);
  const [activeSymptomTracker, setActiveSymptomTracker] = useState(false);
  const [currentSymptoms, setCurrentSymptoms] = useState({
    pain: 0,
    mood: '',
    flow: '',
    energy: 0,
    symptoms: []
  });
  const [showPeriodProducts, setShowPeriodProducts] = useState(false);
  
  const carousels = SexualHealthCarousels();
  
  const tabs = [
    { id: 'overview', label: 'Start Here', icon: BookOpen },
    { id: 'anatomy', label: 'Body Basics', icon: User },
    { id: 'contraception', label: 'Protection', icon: Shield },
    { id: 'sti', label: 'STI Info', icon: Stethoscope },
    { id: 'pleasure', label: 'Pleasure Guide', icon: Star },
    { id: 'safety', label: 'Stay Safe', icon: Shield },
    { id: 'period', label: 'Period Tracker', icon: Calendar },
    { id: 'myths', label: 'Myth Busters', icon: Zap },
    { id: 'facts', label: 'Did You Know?', icon: Brain },
    { id: 'daily', label: 'Daily Tips', icon: Coffee },
  ];



  const renderOverview = () => (
    <div className="space-y-6 sm:space-y-8">
   

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[
          { 
            title: "Body Basics", 
            icon: User, 
            action: () => setActiveTab('anatomy'), 
            color: "blue", 
            description: "Learn about your body"
          },
          { 
            title: "Protection", 
            icon: Shield, 
            action: () => setActiveTab('contraception'), 
            color: "green", 
            description: "Contraception methods"
          },
          { 
            title: "Pleasure Guide", 
            icon: Star, 
            action: () => setActiveTab('pleasure'), 
            color: "yellow", 
            description: "Intimacy & satisfaction"
          },
          { 
            title: "STI Prevention", 
            icon: Stethoscope, 
            action: () => setActiveTab('sti'), 
            color: "red", 
            description: "Stay safe and informed"
          }
        ].map((action, index) => (
          <motion.button
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={action.action}
            className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 ${
              action.color === 'blue' ? 'border-blue-500' :
              action.color === 'green' ? 'border-green-500' :
              action.color === 'yellow' ? 'border-yellow-500' :
              action.color === 'red' ? 'border-red-500' : 'border-gray-500'
            } group relative`}
          >
            <action.icon className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 ${
              action.color === 'blue' ? 'text-blue-600' :
              action.color === 'green' ? 'text-green-600' :
              action.color === 'yellow' ? 'text-yellow-600' :
              action.color === 'red' ? 'text-red-600' : 'text-gray-600'
            } group-hover:scale-110 transition-transform`} />
            <h3 className="font-semibold text-gray-900 mb-2">{action.title}</h3>
            <p className="text-sm text-gray-600">{action.description}</p>
          </motion.button>
        ))}
      </div>
    </div>
  );
  
 

  const renderMyths = () => carousels.myths;

  

  const renderFacts = () => carousels.facts;

  const renderDaily = () => <DailyTipsCarousel />;

  const calculatePeriodPredictions = (lastPeriodDate, cycleLength, periodLength) => {
    if (!lastPeriodDate) return null;
    
    const startDate = new Date(lastPeriodDate);
    const today = new Date();
    
    const nextPeriod = new Date(startDate);
    nextPeriod.setDate(startDate.getDate() + parseInt(cycleLength));
    
    const ovulationDate = new Date(nextPeriod);
    ovulationDate.setDate(nextPeriod.getDate() - 14);
    
    const fertileStart = new Date(ovulationDate);
    fertileStart.setDate(ovulationDate.getDate() - 5);
    
    const fertileEnd = new Date(ovulationDate);
    fertileEnd.setDate(ovulationDate.getDate() + 1);
    
    const daysUntilPeriod = Math.ceil((nextPeriod - today) / (1000 * 60 * 60 * 24));
    
    const currentCycleDay = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24)) + 1;
    
    return {
      nextPeriod,
      ovulationDate,
      fertileStart,
      fertileEnd,
      daysUntilPeriod,
      currentCycleDay,
      cycleProgress: (currentCycleDay / cycleLength) * 100
    };
  };

  const handlePeriodCalculation = () => {
    const results = calculatePeriodPredictions(
      periodData.lastPeriodDate,
      periodData.cycleLength,
      periodData.periodLength
    );
    setPredictions(results);
  };

  const renderPeriodTracker = () => (
    <div className="space-y-6 sm:space-y-8">
      {/* Enhanced Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Complete Menstrual Health Hub</h2>
            <p className="text-xs sm:text-sm text-gray-600">Your comprehensive guide to period tracking, products, pain management & wellness</p>
          </div>
        </div>
        
        {/* Quick Navigation */}
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            { label: 'Calculator', active: !activeSymptomTracker && !showPeriodProducts, action: () => { setActiveSymptomTracker(false); setShowPeriodProducts(false); } },
            { label: 'Symptom Tracker', active: activeSymptomTracker, action: () => { setActiveSymptomTracker(true); setShowPeriodProducts(false); } },
            { label: 'Period Products', active: showPeriodProducts, action: () => { setShowPeriodProducts(true); setActiveSymptomTracker(false); } }
          ].map((nav, index) => (
            <button
              key={index}
              onClick={nav.action}
              className={`px-3 py-1 rounded-full text-sm transition-all ${
                nav.active 
                  ? 'bg-pink-600 text-white' 
                  : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
              }`}
            >
              {nav.label}
            </button>
          ))}
        </div>
      </div>

      {/* Symptom Tracker */}
      {activeSymptomTracker && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Activity className="w-6 h-6 text-purple-600" />
            Daily Symptom Tracker
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pain Level (0-10)
                </label>
                <div className="flex gap-2 flex-wrap">
                  {[...Array(11)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentSymptoms({...currentSymptoms, pain: i})}
                      className={`w-8 h-8 rounded-full text-xs font-medium transition-all ${
                        currentSymptoms.pain === i 
                          ? 'bg-red-500 text-white' 
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>No pain</span>
                  <span>Unbearable</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flow Intensity
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Light', color: 'bg-pink-200', description: 'Spotting or minimal flow' },
                    { label: 'Normal', color: 'bg-pink-400', description: 'Regular flow' },
                    { label: 'Heavy', color: 'bg-pink-600', description: 'Soaking pad/tampon frequently' }
                  ].map((flow) => (
                    <button
                      key={flow.label}
                      onClick={() => setCurrentSymptoms({...currentSymptoms, flow: flow.label})}
                      className={`py-3 px-3 rounded-lg text-sm transition-all ${
                        currentSymptoms.flow === flow.label
                          ? `${flow.color} text-white`
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                      title={flow.description}
                    >
                      {flow.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Energy Level (1-10): {currentSymptoms.energy}
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={currentSymptoms.energy}
                  onChange={(e) => setCurrentSymptoms({...currentSymptoms, energy: parseInt(e.target.value)})}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Exhausted</span>
                  <span>Energized</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mood Today
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['Happy', 'Sad', 'Irritable', 'Anxious', 'Calm', 'Emotional'].map((mood) => (
                    <button
                      key={mood}
                      onClick={() => setCurrentSymptoms({...currentSymptoms, mood})}
                      className={`py-2 px-3 rounded-lg text-sm transition-all ${
                        currentSymptoms.mood === mood
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {mood}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Symptoms (Check all that apply)
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {[
                  '😣 Cramps', '🤕 Headache', '🎈 Bloating', '😤 Mood swings',
                  '😴 Fatigue', '💔 Breast tenderness', '🔙 Back pain', '🤢 Nausea',
                  '🍕 Food cravings', '😠 Irritability', '🔴 Acne', '😳 Hot flashes',
                  '🥶 Chills', '💧 Water retention', '🦴 Joint pain', '😵 Dizziness',
                  '🛏️ Insomnia', '🧠 Brain fog', '💪 Muscle aches', '🍫 Sugar cravings'
                ].map((symptom) => (
                  <label key={symptom} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={currentSymptoms.symptoms.includes(symptom)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCurrentSymptoms({
                            ...currentSymptoms,
                            symptoms: [...currentSymptoms.symptoms, symptom]
                          });
                        } else {
                          setCurrentSymptoms({
                            ...currentSymptoms,
                            symptoms: currentSymptoms.symptoms.filter(s => s !== symptom)
                          });
                        }
                      }}
                      className="rounded text-purple-600"
                    />
                    <span>{symptom}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-6 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all">
              Save Today's Symptoms
            </button>
            <button 
              onClick={() => setCurrentSymptoms({ pain: 0, mood: '', flow: '', energy: 0, symptoms: [] })}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
            >
              Clear
            </button>
          </div>

          {/* Symptom Summary */}
          {(currentSymptoms.pain > 0 || currentSymptoms.symptoms.length > 0) && (
            <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-2">Today's Summary</h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Pain:</span> {currentSymptoms.pain}/10 
                  {currentSymptoms.pain >= 7 ? ' (Severe - consider pain relief)' : 
                   currentSymptoms.pain >= 4 ? ' (Moderate)' : ' (Mild)'}
                </div>
                <div>
                  <span className="font-medium">Energy:</span> {currentSymptoms.energy}/10
                </div>
                <div>
                  <span className="font-medium">Flow:</span> {currentSymptoms.flow || 'Not specified'}
                </div>
                <div>
                  <span className="font-medium">Mood:</span> {currentSymptoms.mood || 'Not specified'}
                </div>
              </div>
              {currentSymptoms.symptoms.length > 0 && (
                <div className="mt-2">
                  <span className="font-medium">Symptoms:</span> {currentSymptoms.symptoms.join(', ')}
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Period Products Guide */}
      {showPeriodProducts && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              🩸 Complete Period Products Guide
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  name: 'Menstrual Cups',
                  emoji: '🏆',
                  cost: '₦8,000-16,000 (lasts years)',
                  capacity: '1-2 oz (30-60ml)',
                  duration: '12 hours',
                  pros: ['Eco-friendly', 'Cost-effective long-term', 'No odor', 'High capacity', 'No dryness'],
                  cons: ['Learning curve', 'Initial investment', 'Need sterilization', 'Public restroom challenges'],
                  bestFor: 'Heavy flow, environmentally conscious, cost-savers, active lifestyle',
                  sustainability: '⭐⭐⭐⭐⭐',
                  comfort: '⭐⭐⭐⭐☆'
                },
                {
                  name: 'Tampons',
                  emoji: '🔴',
                  cost: '₦2,000-3,200/month',
                  capacity: '5-18ml',
                  duration: '4-8 hours',
                  pros: ['Convenient', 'Discreet', 'Good for sports', 'Easy disposal', 'Widely available'],
                  cons: ['TSS risk if left too long', 'Can be drying', 'Ongoing cost', 'Environmental impact'],
                  bestFor: 'Active lifestyle, first-time users, convenience, swimming',
                  sustainability: '⭐⭐☆☆☆',
                  comfort: '⭐⭐⭐⭐☆'
                },
                {
                  name: 'Pads',
                  emoji: '🩹',
                  cost: '₦1,200-2,400/month',
                  capacity: '5-15ml',
                  duration: '4-6 hours',
                  pros: ['Easy to use', 'No insertion', 'Good for heavy flow', 'Various sizes', 'No TSS risk'],
                  cons: ['Can feel bulky', 'Less discreet', 'May shift', 'Skin irritation possible'],
                  bestFor: 'Beginners, overnight protection, sensitive skin, postpartum',
                  sustainability: '⭐⭐☆☆☆',
                  comfort: '⭐⭐⭐☆☆'
                },
                {
                  name: 'Period Discs',
                  emoji: '💿',
                  cost: '₦3,200-6,000/box',
                  capacity: '1-2 oz (30-60ml)',
                  duration: '12 hours',
                  pros: ['12hr protection', 'Sex during period', 'Less cramping', 'High capacity', 'No strings'],
                  cons: ['Trickier insertion', 'Can be messy', 'More expensive', 'Not reusable (most)'],
                  bestFor: 'Heavy flow, minimal cramping, intimacy during period, extended wear',
                  sustainability: '⭐⭐☆☆☆',
                  comfort: '⭐⭐⭐⭐☆'
                }
              ].map((product, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg p-4 border border-pink-200"
                >
                  <div className="text-center mb-4">
                    <div className="text-4xl mb-2">{product.emoji}</div>
                    <h4 className="font-bold text-gray-800 text-lg">{product.name}</h4>
                    <p className="text-sm text-purple-600 font-medium">{product.cost}</p>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="bg-white rounded-lg p-2">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><strong>Capacity:</strong> {product.capacity}</div>
                        <div><strong>Duration:</strong> {product.duration}</div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold text-green-700 mb-1">✅ Pros</h5>
                      <ul className="space-y-1">
                        {product.pros.slice(0, 3).map((pro, i) => (
                          <li key={i} className="text-green-600 text-xs">• {pro}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold text-red-700 mb-1">❌ Cons</h5>
                      <ul className="space-y-1">
                        {product.cons.slice(0, 3).map((con, i) => (
                          <li key={i} className="text-red-600 text-xs">• {con}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="bg-white rounded-lg p-2">
                      <h5 className="font-semibold text-blue-700 mb-1">🎯 Best For</h5>
                      <p className="text-blue-600 text-xs">{product.bestFor}</p>
                    </div>

                    <div className="bg-white rounded-lg p-2">
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div><strong>Eco:</strong> {product.sustainability}</div>
                        <div><strong>Comfort:</strong> {product.comfort}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Period Emergency Kit */}
          <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border border-red-200">
            <h4 className="text-xl font-bold text-red-800 mb-6 flex items-center gap-2">
              🆘 Ultimate Period Emergency Kit
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h5 className="font-bold text-red-700 mb-3 flex items-center gap-2">🎒 Always Carry</h5>
                <ul className="space-y-2 text-sm text-red-600">
                  <li>• 2-3 pads/tampons (different absorbencies)</li>
                  <li>• Pain relievers (ibuprofen/naproxen)</li>
                  <li>• Wet wipes or tissues</li>
                  <li>• Spare underwear in ziplock bag</li>
                  <li>• Small heating pad or heat patches</li>
                  <li>• Hand sanitizer</li>
                  <li>• Dark colored leggings/pants</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-red-700 mb-3 flex items-center gap-2">🏠 Keep at Home</h5>
                <ul className="space-y-2 text-sm text-red-600">
                  <li>• Various product sizes & types</li>
                  <li>• Hot water bottle</li>
                  <li>• Comfort foods (dark chocolate, tea)</li>
                  <li>• Comfortable period underwear</li>
                  <li>• Stain remover (hydrogen peroxide)</li>
                  <li>• Heating pad for bed</li>
                  <li>• Magnesium supplements</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-red-700 mb-3 flex items-center gap-2">🏢 Workplace/School Tips</h5>
                <ul className="space-y-2 text-sm text-red-600">
                  <li>• Desk drawer emergency stash</li>
                  <li>• Know all bathroom locations</li>
                  <li>• Comfortable sitting cushion</li>
                  <li>• Backup clothes in locker/car</li>
                  <li>• Trusted friend for emergencies</li>
                  <li>• Period tracking app reminders</li>
                  <li>• Discreet carrying pouch</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-lg p-4">
              <h6 className="font-bold text-red-800 mb-2">🚨 Emergency Stain Removal</h6>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Fresh stains:</strong> Cold water + salt or hydrogen peroxide
                </div>
                <div>
                  <strong>Set stains:</strong> Enzyme detergent + cold water soak
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {!activeSymptomTracker && !showPeriodProducts && (
        <>
          <div className="grid lg:grid-cols-2 gap-6">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Calendar className="w-6 h-6 text-pink-600" />
                Smart Period Calculator
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First day of last period
                  </label>
                  <input
                    type="date"
                    value={periodData.lastPeriodDate}
                    onChange={(e) => setPeriodData({...periodData, lastPeriodDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Average cycle length (days)
                  </label>
                  <select
                    value={periodData.cycleLength}
                    onChange={(e) => setPeriodData({...periodData, cycleLength: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    {[...Array(15)].map((_, i) => (
                      <option key={i} value={21 + i}>{21 + i} days</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Period length (days)
                  </label>
                  <select
                    value={periodData.periodLength}
                    onChange={(e) => setPeriodData({...periodData, periodLength: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    {[...Array(7)].map((_, i) => (
                      <option key={i} value={3 + i}>{3 + i} days</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handlePeriodCalculation}
                  className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white py-3 rounded-lg font-medium hover:from-pink-600 hover:to-rose-600 transition-all"
                >
                  Calculate My Cycle Predictions
                </button>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Target className="w-6 h-6 text-purple-600" />
                Your Personal Insights
              </h3>
              
              {predictions ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Current Cycle Status</h4>
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Day {predictions.currentCycleDay}</span>
                      <span>{Math.round(predictions.cycleProgress)}% complete</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-pink-500 to-purple-500 rounded-full h-3 transition-all duration-500"
                        style={{width: `${Math.min(predictions.cycleProgress, 100)}%`}}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {predictions.cycleProgress < 25 ? 'Menstrual/Follicular Phase' :
                       predictions.cycleProgress < 50 ? 'Pre-Ovulation Phase' :
                       predictions.cycleProgress < 75 ? 'Ovulation/Early Luteal' :
                       'Late Luteal/PMS Phase'}
                    </p>
                  </div>

                  <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                      🩸 Next Period Prediction
                    </h4>
                    <p className="text-red-700 text-lg font-bold mb-1">
                      {predictions.nextPeriod.toLocaleDateString()}
                    </p>
                    <p className="text-red-600 text-sm">
                      {predictions.daysUntilPeriod > 0 
                        ? `Coming in ${predictions.daysUntilPeriod} days`
                        : predictions.daysUntilPeriod === 0 
                        ? 'Expected today!' 
                        : `${Math.abs(predictions.daysUntilPeriod)} days overdue`
                      }
                    </p>
                    {predictions.daysUntilPeriod <= 3 && predictions.daysUntilPeriod > 0 && (
                      <p className="text-xs text-red-500 mt-1">💡 Time to prep your period kit!</p>
                    )}
                  </div>

                  <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      🌱 Fertility Window
                    </h4>
                    <p className="text-green-700 text-sm font-bold mb-1">
                      {predictions.fertileStart.toLocaleDateString()} - {predictions.fertileEnd.toLocaleDateString()}
                    </p>
                    <p className="text-green-600 text-xs mb-2">
                      Peak fertility: {predictions.ovulationDate.toLocaleDateString()}
                    </p>
                    <div className="bg-green-100 rounded p-2">
                      <p className="text-xs text-green-700">
                        💡 {Math.abs((new Date() - predictions.ovulationDate) / (1000 * 60 * 60 * 24)) <= 1 
                          ? 'Prime conception window!' 
                          : 'Track symptoms for better accuracy'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">📊 Personal Analysis</h4>
                    <div className="space-y-1 text-sm text-blue-700">
                      <p>• Your {periodData.cycleLength}-day cycle is {periodData.cycleLength >= 21 && periodData.cycleLength <= 35 ? '✅ within normal range' : '⚠️ outside typical range - consider consulting a doctor'}</p>
                      <p>• {periodData.periodLength}-day period duration is {periodData.periodLength >= 3 && periodData.periodLength <= 7 ? '✅ normal' : '⚠️ unusual - may need medical advice'}</p>
                      <p>• You have {Math.round((parseInt(periodData.cycleLength) - parseInt(periodData.periodLength)) / parseInt(periodData.cycleLength) * 100)}% period-free days each cycle</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-12">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">Ready for predictions?</p>
                  <p className="text-sm">Enter your period details above to get personalized insights</p>
                </div>
              )}        </motion.div>
      </div>


      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          Period Pain Management Guide
        </h3>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
            <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
              🌿 Natural Remedies
            </h4>
            <ul className="space-y-2 text-sm text-green-700">
              <li>• Heat therapy (heating pad, hot bath)</li>
              <li>• Gentle exercise & yoga</li>
              <li>• Herbal teas (chamomile, ginger)</li>
              <li>• Massage with essential oils</li>
              <li>• Magnesium supplements</li>
              <li>• Anti-inflammatory foods</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
              💊 Medical Options
            </h4>
            <ul className="space-y-2 text-sm text-blue-700">
              <li>• Ibuprofen (anti-inflammatory)</li>
              <li>• Naproxen (longer-lasting)</li>
              <li>• Hormonal birth control</li>
              <li>• Prescription pain relievers</li>
              <li>• Antispasmodic medications</li>
              <li>• Consult healthcare provider</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-4 border border-orange-200">
            <h4 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
              🚨 See Doctor If
            </h4>
            <ul className="space-y-2 text-sm text-orange-700">
              <li>• Pain interferes with daily life</li>
              <li>• Pain worsens over time</li>
              <li>• Heavy bleeding (soaking pad hourly)</li>
              <li>• Severe nausea/vomiting</li>
              <li>• Fever with period pain</li>
              <li>• Pain between periods</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
        <h3 className="text-xl font-bold text-purple-800 mb-6 flex items-center gap-2">
          Understanding Irregular Periods
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-purple-700 mb-3">Common Causes</h4>
            <div className="space-y-3">
              {[
                { condition: 'PCOS', description: 'Hormonal disorder affecting ovulation' },
                { condition: 'Thyroid Issues', description: 'Over/underactive thyroid affects cycles' },
                { condition: 'Stress', description: 'High stress can disrupt hormone balance' },
                { condition: 'Weight Changes', description: 'Significant gain/loss affects periods' },
                { condition: 'Exercise', description: 'Excessive exercise can suppress ovulation' },
                { condition: 'Birth Control', description: 'Hormonal contraceptives affect cycles' }
              ].map((cause, index) => (
                <div key={index} className="bg-white rounded-lg p-3">
                  <h5 className="font-semibold text-gray-800 text-sm">{cause.condition}</h5>
                  <p className="text-gray-600 text-xs">{cause.description}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-purple-700 mb-3">When to Seek Help</h4>
            <div className="bg-white rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span className="text-sm text-gray-700">No period for 3+ months (not pregnant)</span>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span className="text-sm text-gray-700">Cycles shorter than 21 or longer than 35 days</span>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span className="text-sm text-gray-700">Sudden changes in your normal pattern</span>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span className="text-sm text-gray-700">Extremely heavy or painful periods</span>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

  
    </div>
  );
  
  return (
    <Layout>
      <div className="min-h-screen p-6">
        <div className="max-w-8xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Sexual Health Hub</h1>
                <p className="text-xs sm:text-sm text-gray-600">Your comprehensive health education resource</p>
              </div>
            </div>
          </div>
         
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg mb-8 p-2">
            <div className="flex flex-wrap gap-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors relative ${
                    activeTab === tab.id
                      ? 'bg-pink-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'anatomy' && <AnatomySection />}
              {activeTab === 'contraception' && renderContraception()}
              {activeTab === 'sti' && renderSTI()}
              {activeTab === 'pleasure' && renderPleasure()}
              {activeTab === 'safety' && renderSafety()}
              {activeTab === 'period' && renderPeriodTracker()}
              {activeTab === 'myths' && renderMyths()}
              {activeTab === 'facts' && renderFacts()}
              {activeTab === 'daily' && renderDaily()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
};

export default SexualHealthEducation;